import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeftOutlined,
  PrinterOutlined,
  SearchOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { Button, Col, DatePicker, Flex, Form, Row, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';
import {
  AButton,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../../components';
import { TreatmentsListReport } from '../../../../shared/reports/Treatment';
import { useCsvExport } from '../../../../hooks/useCsvExport';

const { RangePicker } = DatePicker;

const TreatmentsReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<string>('');
  const [form] = Form.useForm();
  const { exportCsv } = useCsvExport();

  const handleSearch = useCallback(
    async (values: any) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (values.period) {
          params.append('startDate', values.period[0].format('YYYY-MM-DD'));
          params.append('endDate', values.period[1].format('YYYY-MM-DD'));
          setPeriod(
            `${values.period[0].format('DD/MM/YYYY')} a ${values.period[1].format('DD/MM/YYYY')}`,
          );
        }
        if (values.type !== undefined) params.append('type', values.type);
        if (values.approval !== undefined)
          params.append('approval', values.approval);

        const response = await api.get(
          `/treatments-reports/treatments?${params.toString()}`,
        );
        setData(response.data);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao carregar relatório',
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    const initialValues = {
      period: [dayjs().startOf('month'), dayjs().endOf('month')],
    };
    form.setFieldsValue(initialValues);
    handleSearch(initialValues);
  }, [form, handleSearch]);

  const handleExport = () => {
    exportCsv(
      data,
      ['Data', 'Cliente', 'Profissional', 'Status', 'Valor'],
      'relatorio_tratamentos.csv',
      (item) => [
        dayjs(item.created_at).format('DD/MM/YYYY'),
        `"${item.client_name}"`,
        `"${item.professional_name || '-'}"`,
        item.approval ? 'Aprovado' : 'Pendente',
        item.total?.toString().replace('.', ','),
      ],
    );
  };

  return (
    <PageLayout>
      <PageIntro
        badge="Relatórios"
        icon={<PrinterOutlined />}
        title="Tratamentos"
        description="Filtre por período, tipo e aprovação para analisar tratamentos no novo padrão visual interno."
        actions={
          <AButton
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/treatments')}
          >
            Voltar
          </AButton>
        }
      />
      <PagePanel>
        <Form
          form={form}
          onFinish={handleSearch}
          layout="vertical"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="period"
                label="Período"
                rules={[{ required: true }]}
              >
                <RangePicker
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item name="type" label="Tipo">
                <Select allowClear placeholder="Todos">
                  <Select.Option value={0}>Orçamento</Select.Option>
                  <Select.Option value={1}>Tratamento</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item name="approval" label="Aprovação">
                <Select allowClear placeholder="Todos">
                  <Select.Option value={true}>Aprovado</Select.Option>
                  <Select.Option value={false}>Recusado</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                  loading={loading}
                  block
                >
                  Filtrar
                </Button>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Form.Item label=" ">
                <Button
                  icon={<ExportOutlined />}
                  onClick={handleExport}
                  disabled={!data.length}
                  block
                >
                  Exportar CSV
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <TreatmentsListReport data={data} period={period} />
      </PagePanel>
    </PageLayout>
  );
};

export default TreatmentsReportPage;
